import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { ExamDetail, examDetailRows } from '../../utils/examDetail';

/**
 * The one paper an "Upcoming Exam" alert opened (utils/examDetail), above the full schedule.
 * App and web both draw it.
 */
export function ExamDetailCard({ detail }: { detail: ExamDetail }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  return (
    <View style={styles.card} testID="exam-detail-card">
      <View style={styles.head}>
        <View style={styles.icon}>
          <FeatherIcon name="file-text" size={18} color={colors.primary} />
        </View>
        <View style={styles.fill}>
          <Text style={styles.title}>{detail.subject}</Text>
          <Text style={styles.sub}>{`${detail.examName} · ${detail.date}`}</Text>
        </View>
      </View>
      {examDetailRows(detail).map(row => (
        <View key={row.label} style={styles.row}>
          <Text style={styles.label}>{row.label}</Text>
          <Text style={[styles.value, row.value === 'Not set yet' && styles.unset]}>{row.value}</Text>
        </View>
      ))}
      <View style={styles.note}>
        <FeatherIcon name="info" size={13} color={colors.textSecondary} />
        <Text style={styles.noteText}>No instructions or documents have been shared for this exam yet.</Text>
      </View>
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  card: {
    backgroundColor: colors.card,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.primary,
    padding: 16,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  head: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 10 },
  icon: {
    width: 38, height: 38, borderRadius: 10, backgroundColor: colors.primaryLight,
    alignItems: 'center', justifyContent: 'center',
  },
  fill: { flex: 1, minWidth: 0 },
  title: { fontSize: 16, fontWeight: '800', color: colors.textPrimary },
  sub: { fontSize: 12.5, color: colors.textSecondary, marginTop: 2 },
  row: {
    flexDirection: 'row', justifyContent: 'space-between', gap: 12, paddingVertical: 7,
    borderTopWidth: 1, borderTopColor: colors.divider,
  },
  label: { fontSize: 13, color: colors.textSecondary },
  value: { flex: 1, textAlign: 'right', fontSize: 13, fontWeight: '700', color: colors.textPrimary },
  unset: { fontWeight: '500', color: colors.textMuted },
  note: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 10 },
  noteText: { flex: 1, fontSize: 12, color: colors.textSecondary },
});
